<template>
  <div class="container">
    <div>
      <Header
        title="Claims"
        icon="mdi-loupe"
        iconColor="brand"
        elevation="1"
        titleVariant="h-1"
        cardRounded="md"
        :showSearch="false"
        :showClientDropdown="false"
      />
    </div>
    <v-row>
      <v-col :cols="isOneColumn ? 12 : 4">
        <v-card
          class="action-needed-container"
          v-if="needsActionSubmissions.length > 0"
          data-testid="pax-action-needed-submissions"
        >
          <div class="action-needed-header">
            <v-icon medium left color="warning">mdi-alert</v-icon>
            <Typography variant="h-2">Action Needed</Typography>
          </div>
          <NeedsActionSubmission
            v-for="claim in needsActionSubmissions"
            :key="claim.formSubmissionId"
            :submission="claim"
            @claim-updated="fetchAllInitialClaims"
          />
        </v-card>
      </v-col>
      <v-col :cols="isOneColumn || needsActionSubmissions.length === 0 ? 12 : 8">
        <ParticipantClaims
          :approvedClaims="approvedClaims"
          :pendingClaims="pendingClaims"
          :deniedClaims="deniedClaims"
          :approvedClaimOptions="approvedClaimOptions"
          :pendingClaimOptions="pendingClaimOptions"
          :deniedClaimOptions="deniedClaimOptions"
          :isLoading="isLoading"
          @update:approvedClaimOptions="approvedClaimOptions = $event"
          @update:pendingClaimOptions="pendingClaimOptions = $event"
          @update:deniedClaimOptions="deniedClaimOptions = $event"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Header from "@/components/Header/Header.vue";
import { NeedsActionSubmission } from "@/components/program/participant/submissions";
import Typography from "@/components/generic/Typography.vue";
import ParticipantClaims from "../../components/program/participant/submissions/claims/ParticipantClaims/ParticipantClaims.vue";
import { mapState } from "vuex";
import ProgramClaimService from "@/services/ProgramClaimService";

export default {
  name: "Claims",
  title: "Claims",
  components: {
    Header,
    NeedsActionSubmission,
    Typography,
    ParticipantClaims,
  },
  data() {
    return {
      sortBy: "updateDate",
      isOneColumn: false,
      approvedClaims: [],
      pendingClaims: [],
      deniedClaims: [],
      needsActionSubmissions: [],
      approvedClaimOptions: {
        total: 0,
        page: 1,
        itemsPerPage: 10,
        sortOrder: "desc",
      },
      pendingClaimOptions: {
        total: 0,
        page: 1,
        itemsPerPage: 10,
        sortOrder: "desc",
      },
      deniedClaimOptions: {
        total: 0,
        page: 1,
        itemsPerPage: 10,
        sortOrder: "desc",
      },
      isLoading: true,
    };
  },
  computed: {
    ...mapState(["userProfile"]),
    needsActionClaims() {
      return this.mockSubmissions.filter((submission) =>
        submission.claims.some((claim) => claim.status === "Needs More Info")
      );
    },
  },
  watch: {
    "approvedClaimOptions.sortOrder"() {
      this.approvedClaimOptions.page = 1;
      this.fetchApprovedClaims();
    },
    "pendingClaimOptions.sortOrder"() {
      this.pendingClaimOptions.page = 1;
      this.fetchPendingClaims();
    },
    "deniedClaimOptions.sortOrder"() {
      this.deniedClaimOptions.page = 1;
      this.fetchDeniedClaims();
    },
    "approvedClaimOptions.page": "fetchApprovedClaims",
    "pendingClaimOptions.page": "fetchPendingClaims",
    "deniedClaimOptions.page": "fetchDeniedClaims",
    "approvedClaimOptions.itemsPerPage"() {
      this.approvedClaimOptions.page = 1;
      this.fetchApprovedClaims();
    },
    "pendingClaimOptions.itemsPerPage"() {
      this.pendingClaimOptions.page = 1;
      this.fetchPendingClaims();
    },
    "deniedClaimOptions.itemsPerPage"() {
      this.deniedClaimOptions.page = 1;
      this.fetchDeniedClaims();
    },
    sortBy: "fetchClaims",
  },
  methods: {
    getMockClaims(status) {
      return this.mockSubmissions
        .map((submission) => submission.claims)
        .flat()
        .filter((claim) => claim.status === status);
    },
    onResize() {
      this.isOneColumn = window.innerWidth <= 1366;
    },
    async getClaims({ reviewStatus, formSubmissionId, sortBy, sortOrder, offset, limit }) {
      const claims = await ProgramClaimService.getProgramClaims({
        userId: this.userProfile.userId,
        reviewStatus,
        formSubmissionId,
        sortBy,
        sortOrder,
        offset,
        limit,
      });

      return claims;
    },
    async fetchSubmissions({ reviewStatus }) {
      const submissions = await ProgramClaimService.getFormSubmissions({
        reviewStatus,
      });
      return submissions;
    },
    async fetchAllInitialClaims() {
      try {
        this.isLoading = true;
        const approved = await this.getClaims({
          reviewStatus: "Approved",
          sortBy: this.sortBy,
          sortOrder: this.approvedClaimOptions.sortOrder,
        });
        const pending = await this.getClaims({
          reviewStatus: "Pending",
          sortBy: this.sortBy,
          sortOrder: this.pendingClaimOptions.sortOrder,
        });
        const denied = await this.getClaims({
          reviewStatus: "Denied",
          sortBy: this.sortBy,
          sortOrder: this.deniedClaimOptions.sortOrder,
        });
        const submissions = await this.fetchSubmissions({
          reviewStatus: "Needs More Info",
        });

        this.needsActionSubmissions = submissions.rows;

        console.log("Needs Action Submissions ", this.needsActionSubmissions);
        // only set all claims if there are no errors
        this.approvedClaims = approved.rows;
        this.approvedClaimOptions.total = approved.count;
        this.pendingClaims = pending.rows;
        this.pendingClaimOptions.total = pending.count;
        this.deniedClaims = denied.rows;
        this.deniedClaimOptions.total = denied.count;
      } catch (error) {
        console.error("Error fetching claims", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error fetching claims",
          color: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchApprovedClaims() {
      try {
        this.isLoading = true;
        const offset =
          (this.approvedClaimOptions.page - 1) * this.approvedClaimOptions.itemsPerPage;
        const result = await this.getClaims({
          reviewStatus: "Approved",
          sortBy: this.sortBy,
          sortOrder: this.approvedClaimOptions.sortOrder,
          offset,
          limit: this.approvedClaimOptions.itemsPerPage,
        });
        this.approvedClaims = result.rows;
        this.approvedClaimOptions.total = result.count;
      } catch (error) {
        console.error("Error fetching approved claims", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error fetching approved claims",
          color: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPendingClaims() {
      try {
        this.isLoading = true;
        const offset = (this.pendingClaimOptions.page - 1) * this.pendingClaimOptions.itemsPerPage;
        const result = await this.getClaims({
          reviewStatus: "Pending",
          sortBy: this.sortBy,
          sortOrder: this.pendingClaimOptions.sortOrder,
          offset,
          limit: this.pendingClaimOptions.itemsPerPage,
        });
        this.pendingClaims = result.rows;
        this.pendingClaimOptions.total = result.count;
      } catch (error) {
        console.error("Error fetching pending claims", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error fetching pending claims",
          color: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDeniedClaims() {
      try {
        this.isLoading = true;
        const offset = (this.deniedClaimOptions.page - 1) * this.deniedClaimOptions.itemsPerPage;
        const result = await this.getClaims({
          reviewStatus: "Denied",
          sortBy: this.sortBy,
          sortOrder: this.deniedClaimOptions.sortOrder,
          offset,
          limit: this.deniedClaimOptions.itemsPerPage,
        });
        this.deniedClaims = result.rows;
        this.deniedClaimOptions.total = result.count;
      } catch (error) {
        console.error("Error fetching denied claims", error);
        this.$store.dispatch("showSnackbar", {
          text: "Error fetching denied claims",
          color: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;

    window.removeEventListener("resize", this.onResize, { passive: true });
  },
  async mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
    await this.fetchAllInitialClaims();
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 100%;
}

.action-needed-container {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
}

.action-needed-header {
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 0.75rem;
}
</style>
